// generated by the plop

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { ReceiptContainerModel, ReceiptsService, TemplateModel, TRAFFIC_LOCAL_STORAGE_KEY } from 'shared';

import { PROGRESS_STATUSES } from 'symbols';
import {
  CreatedTrafficByWS,
  CreateTraffic,
  CreateTrafficFail,
  CreateTrafficSuccess,
  CreateTrafficTemplate,
  DeleteLocalTraffic,
  DeleteTrafficTemplate,
  GetTrafficById,
  GetTrafficByIdFail,
  GetTrafficByIdSuccess,
  GetTrafficTemplateList,
  SetCurrentTrafficId,
  UpdatedTrafficByWS,
  UpdateLocalTraffic,
  UpdateTraffic,
  UpdateTrafficFail,
  UpdateTrafficSuccess,
  UpdateTrafficTemplate,
  UpdateTrafficTemplateFail,
  UpdateTrafficTemplateSuccess
} from './traffic-manage.actions';
import { TRAFFIC_MANAGE_STATE_DEFAULT, TrafficManageStateModel } from './traffic-manage.model';


@State({ name: 'TrafficManage', defaults: TRAFFIC_MANAGE_STATE_DEFAULT })
@Injectable()
export class TrafficManageState {

  constructor(private trafficsService: ReceiptsService, private store: Store) {
  }

  @Selector()
  static trafficTemplateList(state: TrafficManageStateModel): TemplateModel[] {
    return state.trafficTemplateList;
  }


  @Selector()
  static currentTraffic(state: TrafficManageStateModel): ReceiptContainerModel {
    return state.traffics?.[state.currentTrafficId];
  }

  @Selector()
  static loadTrafficStatus(state: TrafficManageStateModel): PROGRESS_STATUSES {
    return state.loadTrafficStatus;
  }


  @Action(GetTrafficById)
  getTrafficById(ctx: StateContext<TrafficManageStateModel>, { id }: GetTrafficById): void {
    ctx.patchState({
      loadTrafficStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    if (ctx.getState().traffics?.[id]?.id) {
      this.trafficsService.getTrafficById(id).subscribe({
        next: traffic => {
          if (traffic) {
            ctx.dispatch(new UpdatedTrafficByWS(traffic));
          } else {
            ctx.patchState({
              loadTrafficStatus: PROGRESS_STATUSES.SUCCESS
            });
          }
          ctx.patchState({
            currentTrafficId: id
          });
        },
        error: err => ctx.dispatch(new GetTrafficByIdFail(err))
      });
    } else {
      this.trafficsService.getTrafficById(id).subscribe({
        next: traffic => ctx.dispatch(new GetTrafficByIdSuccess(traffic)),
        error: err => ctx.dispatch(new GetTrafficByIdFail(err))
      });
    }
  }

  @Action(GetTrafficByIdSuccess)
  getTrafficByIdSuccess(ctx: StateContext<TrafficManageStateModel>, { traffic }: GetTrafficByIdSuccess): void {
    const traffics = cloneDeep(ctx.getState().traffics);
    traffics[traffic.id] = traffic;
    ctx.patchState({
      traffics,
      currentTrafficId: traffic.id,
      loadTrafficStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetTrafficByIdFail)
  getTrafficByIdFail(ctx: StateContext<TrafficManageStateModel>, { error }: GetTrafficByIdFail): void {
    ctx.patchState({
      loadTrafficStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetCurrentTrafficId)
  setCurrentTrafficId(ctx: StateContext<TrafficManageStateModel>, { id }: SetCurrentTrafficId): void {
    ctx.patchState({
      currentTrafficId: id
    });
  }

  @Action(UpdateLocalTraffic)
  updateLocalTraffic(ctx: StateContext<TrafficManageStateModel>, { traffic }: UpdateLocalTraffic): void {
    const traffics = cloneDeep(ctx.getState().traffics);
    traffics[traffic.id] = { ...traffics[traffic.id], ...traffic };
    localStorage.setItem(TRAFFIC_LOCAL_STORAGE_KEY, JSON.stringify(traffics));
    ctx.patchState({
      traffics
    });
  }

  @Action(DeleteLocalTraffic)
  deleteLocalTraffic(ctx: StateContext<TrafficManageStateModel>, { id }: DeleteLocalTraffic): void {
    const traffics = cloneDeep(ctx.getState().traffics);

    delete traffics[id];
    ctx.patchState({
      traffics
    });
  }


  @Action(UpdateTraffic)
  updateTraffic(ctx: StateContext<TrafficManageStateModel>, { id, traffic, force }: UpdateTraffic): void {
    ctx.patchState({
      updateTrafficStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.trafficsService.updateTraffic(id, traffic, force).subscribe({
      next: updatedTraffic => ctx.dispatch(new UpdateTrafficSuccess(updatedTraffic)),
      error: err => ctx.dispatch(new UpdateTrafficFail(err))
    });
  }

  @Action(UpdateTrafficSuccess)
  updateTrafficSuccess(ctx: StateContext<TrafficManageStateModel>, { traffic }: UpdateTrafficSuccess): void {
    const traffics = cloneDeep(ctx.getState().traffics);
    ctx.patchState({
      updateTrafficStatus: PROGRESS_STATUSES.SUCCESS,
      traffics: { ...traffics, [traffic.id]: { ...traffics[traffic.id] } }
    });
  }

  @Action([UpdatedTrafficByWS])
  updateTrafficByWS(ctx: StateContext<TrafficManageStateModel>, { traffic }: UpdatedTrafficByWS): void {
    const traffics = cloneDeep(ctx.getState().traffics);
    const trafficExist = traffics[traffic.id];

    if (trafficExist) {
      ctx.patchState({
        loadTrafficStatus: PROGRESS_STATUSES.SUCCESS,
        traffics: {
          ...traffics,
          [traffic.id]: { ...traffics[traffic.id] }
        }
      });
    }
    ctx.patchState({
      loadTrafficStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(CreatedTrafficByWS)
  createdTrafficByWS(ctx: StateContext<TrafficManageStateModel>, { traffic }: CreatedTrafficByWS): void {
    const traffics = cloneDeep(ctx.getState().traffics);
    ctx.patchState({
      traffics: {
        ...traffics,
        [traffic.id]: {
          ...traffics[traffic.id],
          isChanged: false,
          isNew: false
        }
      }
    });
  }


  @Action(CreateTraffic)
  createTraffic(ctx: StateContext<TrafficManageStateModel>, { traffic }: CreateTraffic): void {
    ctx.patchState({
      updateTrafficStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.trafficsService.createTraffic(traffic).subscribe({
      next: createdTraffic => ctx.dispatch(new CreateTrafficSuccess(createdTraffic)),
      error: err => ctx.dispatch(new CreateTrafficFail(err))
    });
  }

  @Action(CreateTrafficSuccess)
  createTrafficSuccess(ctx: StateContext<TrafficManageStateModel>, { traffic }: CreateTrafficSuccess): void {
    ctx.patchState({
      updateTrafficStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(CreateTrafficFail)
  createTrafficFail(ctx: StateContext<TrafficManageStateModel>, { error }: CreateTrafficFail): void {
    ctx.patchState({
      updateTrafficStatus: PROGRESS_STATUSES.FAIL
    });
  }


  @Action(GetTrafficTemplateList)
  getTrafficTemplateList(ctx: StateContext<TrafficManageStateModel>): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.trafficsService.getTrafficTemplateList().subscribe({
      next: templateList => ctx.patchState({
        trafficTemplateList: templateList,
        loadTemplateListStatus: PROGRESS_STATUSES.SUCCESS
      }),
      error: err => ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL })
    });
  }

  @Action(CreateTrafficTemplate)
  createTrafficTemplate(ctx: StateContext<TrafficManageStateModel>, { template, name }: CreateTrafficTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.trafficsService.createTemplate(name, template).subscribe({
      next: () => ctx.dispatch([new GetTrafficTemplateList(), new UpdateTrafficTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTrafficTemplateFail(err));
      }
    });
  }

  @Action(UpdateTrafficTemplate)
  updateTrafficTemplate(ctx: StateContext<TrafficManageStateModel>, { template, id }: UpdateTrafficTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.trafficsService.updateTemplate(id, {
      html: template.html,
      project: template.project,
      formatId: template.formatId,
      css: template.css
    }, template.name).subscribe({
      next: () => ctx.dispatch([new GetTrafficTemplateList(), new UpdateTrafficTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTrafficTemplateFail(err));
      }
    });
  }

  @Action(DeleteTrafficTemplate)
  deleteTrafficTemplate(ctx: StateContext<TrafficManageStateModel>, { id }: DeleteTrafficTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.trafficsService.deleteTemplate(id).subscribe({
      next: () => ctx.dispatch([new GetTrafficTemplateList(), new UpdateTrafficTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTrafficTemplateFail(err));
      }
    });
  }

}


